// Feature: M004-F0721. Tests: T-M004-0227, T-M004-0228.
//
// "Raise credit note" on a WRITTEN transaction (design M004-F0712-F0716 section 4.7b; Spec raiseLedgerCreditNote, ACCT-TX-021).
// Amount > 0 (pounds, at most two decimals) and, when the drawer knows it, at most what is left to credit; reason required 1..500.
// SAL 4 through the existing StepUpDialog pattern; the SAME Idempotency-Key is reused on a retry and on the step-up replay (a new
// key only when amount or reason changed, or after the credit note was raised). The UI is CONFIRMED, never optimistic: the credit
// note renders only from the Ledger's answer (`state`). Every answer has its own plain copy; the raw code is never shown.
import React, { useRef, useState } from 'react';
import StepUpDialog from '../auth/StepUpDialog';
import { txHistoryApi } from '../../services/api';
import { errorBody, errorCode, errorStatus, unwrap } from '../../services/txHistoryAdapter';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import { amountText } from './txStages';

export const CREDIT_COPY = Object.freeze({
  helper: (customer) => `This reduces what ${customer || 'the customer'} owes for this cost. It is recorded as a credit note under this same transaction; the original invoice line is unchanged.`,
  amountInvalid: 'Enter an amount above zero, with at most two decimal places.',
  reasonRequired: 'Give a reason (1 to 500 characters).',
  exceeds: (remaining) => (remaining ? `That is more than is left to credit on this cost. Enter an amount up to ${remaining}.` : 'That is more than is left to credit on this cost.'),
  notCreditable: 'This invoice can\'t be credited here, because it has been paid. Corrections to a paid invoice are made in the Ledger.',
  idempotencyConflict: 'This credit note was already sent with different details. Check the page and try again.',
  nothingToCreditFallback: 'There is nothing to credit yet because this cost has not been written to the ledger. To change the amount, edit it instead.',
  generic: 'The credit note could not be saved. Nothing was changed.',
  cancelled: 'Verification was cancelled.',
  raised: (ref) => `Credit note ${ref} raised.`,
});

// Pounds text -> minor units, or null. Positive only; at most 2 decimals.
export function parseCreditAmountMinor(value) {
  const text = String(value ?? '').replace(/[£,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const minor = Math.round(Number(text) * 100);
  return Number.isSafeInteger(minor) && minor >= 1 ? minor : null;
}

const newKey = () => globalThis.crypto?.randomUUID?.() || `credit-note-${Date.now()}-${Math.random()}`;

export default function TxCreditNoteForm({ orgId, userId, handOffId, txId, customerName, remainingMinor, currency, onRaised, onCancel }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const [condition, setCondition] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const keyRef = useRef({ signature: '', key: '' });
  const pending = useRef(null);
  const remainingText = remainingMinor != null ? amountText(remainingMinor, currency || undefined) : '';

  const send = async (amountMinor, trimmedReason) => {
    const signature = `${amountMinor}|${trimmedReason}`;
    if (keyRef.current.signature !== signature) keyRef.current = { signature, key: newKey() };
    pending.current = { amountMinor, trimmedReason };
    setBusy(true); setError(''); setErrorDetail(''); setCondition(null);
    try {
      const response = await txHistoryApi.raiseCreditNote(orgId, userId, handOffId, { amountMinor, reason: trimmedReason }, keyRef.current.key);
      pending.current = null;
      const data = unwrap(response) || {};
      const conditions = Array.isArray(data.conditions) ? data.conditions : [];
      const nothing = conditions.find((c) => c?.code === 'nothing_to_credit');
      if (!data.credit_note) {
        // 200 with no credit note: a CONDITION, never an error element.
        setCondition(nothing || { message: '', next_action: '' });
        return;
      }
      keyRef.current = { signature: '', key: '' }; // the intent is done: the next credit note is a new intent
      setAmount(''); setReason('');
      onRaised({ creditNote: data.credit_note, state: data.state, message: CREDIT_COPY.raised(data.credit_note.document_ref) });
    } catch (cause) {
      const stepUp = getStepUpChallengeFromError(cause);
      if (stepUp) { setChallenge(stepUp); return; }
      const code = errorCode(cause);
      const body = errorBody(cause);
      const status = errorStatus(cause);
      if (status === 409 && code === 'LEDGER_CREDIT_EXCEEDS_LINE') setError(CREDIT_COPY.exceeds(remainingText));
      else if (status === 409 && code === 'LEDGER_INVOICE_NOT_CREDITABLE') { setError(CREDIT_COPY.notCreditable); setErrorDetail(typeof body.message === 'string' ? body.message : ''); }
      else if (status === 409 && code === 'LEDGER_HA_IDEMPOTENCY_CONFLICT') setError(CREDIT_COPY.idempotencyConflict);
      else setError(typeof body.message === 'string' && body.message.trim() ? body.message : CREDIT_COPY.generic);
    } finally {
      setBusy(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (busy) return; // in-flight guard: a double click is ONE request
    const amountMinor = parseCreditAmountMinor(amount);
    const trimmedReason = reason.trim();
    if (amountMinor == null) { setError(CREDIT_COPY.amountInvalid); setCondition(null); return; }
    if (remainingMinor != null && amountMinor > remainingMinor) { setError(CREDIT_COPY.exceeds(remainingText)); setCondition(null); return; }
    if (!trimmedReason || trimmedReason.length > 500) { setError(CREDIT_COPY.reasonRequired); setCondition(null); return; }
    send(amountMinor, trimmedReason);
  };

  return (
    <form className="tx-credit-form" onSubmit={submit} data-testid="tx-credit-form" aria-label={`Raise a credit note for ${txId}`}>
      <StepUpDialog
        challenge={challenge}
        title="Verify credit note"
        description="Confirm your identity before raising this credit note."
        onComplete={() => { const again = pending.current; setChallenge(null); if (again) send(again.amountMinor, again.trimmedReason); }}
        onCancel={() => { pending.current = null; setChallenge(null); setError(CREDIT_COPY.cancelled); }}
      />
      <h4>{`Raise a credit note for ${txId}`}</h4>
      <p className="tx-muted" data-testid="tx-credit-helper">{CREDIT_COPY.helper(customerName)}</p>
      {remainingText ? <p className="tx-muted" data-testid="tx-credit-remaining">{`Left to credit: ${remainingText}`}</p> : null}
      <label>Amount (pounds)
        <input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} aria-label="Amount (pounds)" />
      </label>
      <label>Reason (required)
        <textarea value={reason} maxLength={500} rows={2} onChange={(event) => setReason(event.target.value)} aria-label="Reason (required)" />
      </label>
      {error ? <p className="tx-credit-error" role="alert" data-testid="tx-credit-error">{error}</p> : null}
      {errorDetail ? <p className="tx-muted" data-testid="tx-credit-error-detail">{errorDetail}</p> : null}
      {condition ? (
        <div className="tx-condition" role="status" data-testid="tx-credit-condition">
          <p className="tx-condition-message">{condition.message || CREDIT_COPY.nothingToCreditFallback}</p>
          {condition.message && condition.next_action ? <p className="tx-condition-next">{condition.next_action}</p> : null}
        </div>
      ) : null}
      <div className="tx-credit-buttons">
        <button type="submit" className="btn btn-primary" disabled={busy}>Raise credit note</button>
        <button type="button" className="btn btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}
